import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  SetFilterModule,
  SideBarModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesDollar } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import {
  getInitials,
  LOAN_STATUS_TONE,
  rateDelta,
  STAGE_TONE,
  trimNum,
  typeTone,
} from "./sizerDisplay";

ModuleRegistry.registerModules([
  SideBarModule,
  MultiFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const ChipCell = ({ label, tone, filled }) => {
  if (!label) return <Txt sx={{ color: grey[400] }}>—</Txt>;
  const t = tone || { fg: grey[700], bg: grey[100] };
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 20,
        fontSize: 11,
        fontWeight: 600,
        color: filled ? "#fff" : t.fg,
        background: filled ? "#16a34a" : t.bg,
        border: `1px solid ${filled ? "#16a34a" : t.bg}`,
      }}
    />
  );
};

// File + meta line for the version-history row: filename on top, then the
// Current/Type badges and "· date · initials" attribution beneath it.
const FileHistoryCell = ({ data, isCurrent }) => {
  const users = useSelector((state) => state.users);
  const uploader =
    getUserFullNameFromEmail(users, data?.uploadedBy) || data?.uploadedBy;
  const initials = getInitials(uploader);
  const type = data?.sizer_type_at_upload;
  return (
    <Flx ac g={1} sx={{ height: "100%" }}>
      <DescriptionOutlined
        className="thin"
        sx={{ fontSize: 18, color: grey[500], flexShrink: 0 }}
      />
      <Flx column sx={{ minWidth: 0, lineHeight: 1.3 }}>
        <Tooltip title={data?.file_display_name || ""} placement="top">
          <Txt
            component="div"
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#0f172a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data?.file_display_name || "—"}
          </Txt>
        </Tooltip>
        <Flx ac g={0.5} sx={{ mt: 0.25, rowGap: 0.25 }} wrap>
          {isCurrent && <ChipCell label="Current" filled />}
          {type && <ChipCell label={type} tone={typeTone(type)} />}
          <Txt sx={{ fontSize: 10.5, color: grey[500] }}>
            {[
              data?.uploadDate ? formatDate(data.uploadDate) : null,
              initials,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Txt>
        </Flx>
      </Flx>
    </Flx>
  );
};

const DownloadActionCell = ({ data }) => {
  const { loading, getFileById } = useLoanFilesHook();
  const onDownloadClick = () => {
    getFileById({
      loanId: data?.dealId,
      file_storage_name: data?.file_storage_name,
      fileDownloadName: data?.file_display_name,
      fileDownload: true,
    });
  };
  return (
    <Tooltip title="Download">
      <IconButton
        disabled={loading}
        onClick={onDownloadClick}
        size="small"
        color="primary"
      >
        <DownloadOutlined className="thin-7" sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );
};

// A numeric value with an optional Δ-vs-prior sub-line (shown in compare mode).
const DeltaCell = ({ value, delta }) => (
  <Flx column sx={{ lineHeight: 1.3 }}>
    <Txt component="div" sx={{ fontSize: 12.5, color: "#0f172a" }}>
      {value}
    </Txt>
    {delta && (
      <Txt component="div" sx={{ fontSize: 10.5, color: delta.color }}>
        {delta.text}
      </Txt>
    )}
  </Flx>
);

const LoanSizerUploadsTable = forwardRef(
  (
    { rowData, quickFilter, onRowClicked, currentId, priorById, compareMode },
    ref,
  ) => {
    const columnDefs = useMemo(
      () => [
        {
          headerName: "",
          width: 56,
          maxWidth: 56,
          flex: 0,
          pinned: "right",
          resizable: false,
          sortable: false,
          filter: false,
          suppressHeaderMenuButton: true,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          },
          cellRenderer: (params) => <DownloadActionCell data={params?.data} />,
        },
        {
          headerName: "File",
          field: "file_display_name",
          minWidth: 300,
          flex: 2,
          cellRenderer: (p) => (
            <FileHistoryCell
              data={p?.data}
              isCurrent={p?.data?._id === currentId}
            />
          ),
        },
        {
          headerName: "Stage",
          field: "sizer_stage_at_upload",
          width: 170,
          minWidth: 150,
          cellRenderer: (p) => (
            <ChipCell label={p.value} tone={STAGE_TONE[p.value]} />
          ),
        },
        {
          headerName: "Total Loan",
          field: "upload_totalLoanAmount",
          type: "dollar",
          width: 150,
          cellRenderer: (p) => {
            const prior = priorById?.[p?.data?._id];
            let delta = null;
            if (compareMode && prior) {
              const diff =
                (p.value || 0) - (prior.upload_totalLoanAmount || 0);
              if (Math.abs(diff) >= 1) {
                const up = diff > 0;
                delta = {
                  text: `${up ? "▲" : "▼"} ${formatDollar(Math.abs(diff))} vs. prior`,
                  color: up ? "#b91c1c" : "#047857",
                };
              }
            }
            return <DeltaCell value={formatDollar(p.value, "—")} delta={delta} />;
          },
        },
        {
          headerName: "Rate",
          field: "upload_interestRate",
          width: 130,
          cellRenderer: (p) => {
            const value =
              typeof p.value === "number" ? formatPercent(p.value) : "—";
            const prior = priorById?.[p?.data?._id];
            let delta = null;
            if (compareMode && prior) {
              const d = rateDelta(p.value, prior.upload_interestRate);
              if (d && d.dir !== "flat") {
                const up = d.dir === "up";
                delta = {
                  text: `${up ? "▲" : "▼"} ${trimNum(d.value)} vs. prior`,
                  color: up ? "#b91c1c" : "#047857",
                };
              }
            }
            return <DeltaCell value={value} delta={delta} />;
          },
        },
        {
          headerName: "Points (Orig / Disc)",
          colId: "points",
          width: 160,
          valueGetter: (p) => {
            const orig = p?.data?.upload_originationPoints;
            const disc = p?.data?.upload_discountPoints;
            if (orig == null && disc == null) return "—";
            return `${trimNum(orig ?? 0, 5)} / ${trimNum(disc ?? 0, 5)}`;
          },
        },
        // Extra columns available via the column menu but hidden by default so
        // the history reads clean like the current-sizer summary above it.
        {
          headerName: "Base Loan",
          field: "upload_baseLoanAmount",
          type: "dollar",
          width: 130,
          hide: true,
          valueFormatter: (p) => formatDollar(p.value, "—"),
        },
        {
          headerName: "Purpose",
          field: "upload_loanPurpose",
          width: 120,
          hide: true,
        },
        {
          headerName: "Product Type",
          field: "upload_loanProductType",
          width: 140,
          hide: true,
        },
        {
          headerName: "Loan Status",
          field: "upload_loanStatus",
          width: 140,
          hide: true,
          cellRenderer: (p) => (
            <ChipCell label={p.value} tone={LOAN_STATUS_TONE[p.value]} />
          ),
        },
        {
          headerName: "Expected Close",
          field: "upload_expectedClosingDate",
          type: "date",
          width: 150,
          hide: true,
          valueFormatter: (p) => formatDate(p.value, "—"),
        },
        {
          headerName: "Uploaded",
          field: "uploadDate",
          type: "date",
          width: 150,
          hide: true,
          sort: "desc",
          valueFormatter: (p) => formatDate(p.value, "—"),
        },
      ],
      [currentId, priorById, compareMode],
    );

    const defaultColDef = useMemo(
      () => ({
        sortable: true,
        resizable: true,
        filter: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        flex: 1,
        minWidth: 100,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          lineHeight: "18px",
        },
      }),
      [],
    );

    const columnTypes = useMemo(
      () => ({ ...columnTypesDate(), ...columnTypesDollar() }),
      [],
    );

    const getRowId = useCallback((p) => p?.data?._id, []);

    const handleRowClicked = useCallback(
      (params) => {
        if (typeof onRowClicked === "function") onRowClicked(params?.data);
      },
      [onRowClicked],
    );

    return (
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={rowData}
        compactTable
        sx={{ minWidth: "340px" }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          getRowId={getRowId}
          quickFilterText={quickFilter}
          domLayout="autoHeight"
          onRowClicked={handleRowClicked}
          rowHeight={compareMode ? 66 : 58}
        />
      </AgGridTableWrapper>
    );
  },
);

export default LoanSizerUploadsTable;
